import type { DesktopUpdateRequiredPayload } from '@kiao-voice/api-contract/desktop'
import { TEAM_MEMBER_DISABLED_ERROR } from '@kiao-voice/api-contract/errors'
import {
  ACTIVE_CONTEXT_HEADER,
  clearActiveContext,
  getActiveContextHeaderValue,
} from './active-context.js'

/**
 * The `X-Active-Context` header to merge into a request, or an empty object to
 * OMIT it (until the user explicitly picks a context — see
 * `getActiveContextHeaderValue`). Spreading the empty object adds no header,
 * letting the server apply its personal fallback.
 */
function activeContextHeader(): Record<string, string> {
  const value = getActiveContextHeaderValue()
  return value === null ? {} : { [ACTIVE_CONTEXT_HEADER]: value }
}

/** A 403 from a stale/revoked `X-Active-Context` (server: `active_context_forbidden`). */
function isActiveContextForbidden(error: ApiError): boolean {
  return (
    error.status === 403 &&
    typeof error.body === 'object' &&
    error.body !== null &&
    (error.body as { error?: string }).error === 'active_context_forbidden'
  )
}

function isInvalidActiveContext(error: ApiError): boolean {
  return (
    error.status === 400 &&
    typeof error.body === 'object' &&
    error.body !== null &&
    (error.body as { error?: string }).error === 'invalid_active_context'
  )
}

/**
 * Whether an arbitrary thrown value is a 403 caused by a stale/revoked active
 * context (`active_context_forbidden`). The shared client already CLEARS the
 * stored context on this error, so UIs should treat it as a transient,
 * self-recovering condition (retry → the header is omitted → the server
 * resolves the personal fallback) — NOT as account deactivation / a reason to
 * force sign-out. Exposed so desktop + web 403 handlers can branch on it.
 */
export function isActiveContextForbiddenError(error: unknown): boolean {
  return error instanceof ApiError && isActiveContextForbidden(error)
}

export { TEAM_MEMBER_DISABLED_ERROR }

/**
 * Whether a thrown value is the 403 for a DISABLED team seat
 * (`team_member_disabled`). Unlike `active_context_forbidden`, the shared
 * client neither clears the selected workspace nor retries elsewhere on it, and
 * a UI must not treat it as a sign-out: the workspace is still the member's,
 * only its content and new work are closed. Accepts the shared `ApiError`
 * (code in `body.error`) and the feature wrappers that lift it to `code`.
 */
export function isTeamMemberDisabledError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { status?: unknown; code?: unknown; body?: unknown }
  if (candidate.status !== 403) return false
  if (candidate.code === TEAM_MEMBER_DISABLED_ERROR) return true
  const body = candidate.body
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { error?: unknown }).error === TEAM_MEMBER_DISABLED_ERROR
  )
}

export interface TokenStorage {
  getAccessToken(): Promise<string | null>
  setAccessToken(token: string): Promise<void>
  clearTokens(): Promise<void>
}

/**
 * A {@link TokenStorage} that holds nothing: reads resolve `null` and both
 * writes are no-ops. Pair it with `sendActiveContext: false` to build a client
 * for ANONYMOUS endpoints (landing demo, guest checkout, invite preview, magic
 * login, anonymous trial) — the request then carries neither a bearer token nor
 * an `X-Active-Context`, even when the visitor happens to be signed in, and the
 * no-op writes guarantee such a call can never mutate the real session.
 */
export const anonymousTokenStorage: TokenStorage = {
  async getAccessToken() {
    return null
  },
  async setAccessToken() {},
  async clearTokens() {},
}

/**
 * Context passed alongside an {@link ApiError} to {@link ApiClientConfig.onResponseError}.
 * `requestToken` is the bearer token that was read from `tokenStorage` for the
 * failing request — exposed so callers can implement token-specific self-heal
 * with a race guard (e.g. "only drop the token on a 401 if it's STILL the
 * stored token", so a stale in-flight 401 carrying an old token can't evict a
 * token a parallel sign-in just stored).
 */
export interface ApiErrorContext {
  requestToken: string | null
  /** Exact context header used by the rejected request, before any self-heal. */
  requestActiveContext: string | null
  /** True only when this response cleared the still-current rejected context. */
  didClearActiveContext: boolean
}

export interface ApiClientConfig {
  baseUrl: string
  tokenStorage: TokenStorage
  getDefaultHeaders?: () => Promise<Record<string, string>> | Record<string, string>
  onResponseError?: (error: ApiError, context: ApiErrorContext) => Promise<void> | void
  /**
   * Override the `fetch` implementation (defaults to the global `fetch`,
   * resolved at call time). Primarily a test seam so callers can inject a mock
   * without stubbing the global.
   */
  fetchFn?: typeof fetch
  /**
   * Whether to inject the `X-Active-Context` header (default `true`). Set
   * `false` for a PUBLIC client that talks to anonymous endpoints — they must
   * receive no auth and no context even when the user happens to be signed in
   * (pair with a `tokenStorage` that returns `null`).
   */
  sendActiveContext?: boolean
}

export interface UploadOptions {
  headers?: Record<string, string>
  signal?: AbortSignal
  timeoutMs?: number
  /**
   * Opt-in: when the server refuses the declared `X-Active-Context`
   * (`active_context_forbidden`), clear it and repeat the POST once header-less
   * so the server resolves its personal fallback. OFF by default because a
   * context-scoped write would then land in personal —
   * only enable it where that fallback is acceptable (e.g. a dictation whose
   * audio would otherwise be lost, billed as a header-less dictation would be).
   * Leave it off for uploads whose funding/sharing semantics are chosen BY the
   * context (e.g. a team batch upload): those must surface the 403 instead of
   * silently landing in another workspace.
   */
  retryOnStaleActiveContext?: boolean
}

export interface ApiClient {
  request<T>(path: string, options?: RequestInit): Promise<T>
  /** Like {@link request} but returns the raw response text (for non-JSON
   *  endpoints, e.g. transcript downloads). Same auth/header/error handling. */
  requestText(path: string, options?: RequestInit): Promise<string>
  /** Like {@link request} but returns the raw response Blob (for binary
   *  downloads, e.g. exported audio clips). Same auth/header/error handling. */
  requestBlob(path: string, options?: RequestInit): Promise<Blob>
  upload<T>(path: string, formData: FormData, options?: UploadOptions): Promise<T>
}

export class ApiError extends Error {
  readonly status: number
  readonly statusText: string
  readonly body: unknown

  constructor({
    status,
    statusText,
    body,
    message = `API error: ${status} ${statusText}`,
  }: {
    status: number
    statusText: string
    body: unknown
    message?: string
  }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.body = body
  }
}

/**
 * The 426 body lives in the contract package with every other wire shape. It
 * is re-exported here so `@kiao-voice/api-client` and
 * `@kiao-voice/api-client/client` keep resolving it, as they did when this
 * transport file declared it.
 */
export type { DesktopUpdateRequiredPayload }

export class DesktopUpdateRequiredError extends ApiError {
  readonly payload: DesktopUpdateRequiredPayload

  constructor({
    status,
    statusText,
    payload,
  }: {
    status: number
    statusText: string
    payload: DesktopUpdateRequiredPayload
  }) {
    super({
      status,
      statusText,
      body: payload,
      message: payload.message,
    })
    this.name = 'DesktopUpdateRequiredError'
    this.payload = payload
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { baseUrl, tokenStorage, getDefaultHeaders, onResponseError } = config
  const sendActiveContext = config.sendActiveContext ?? true
  // Resolve the global `fetch` at call time when no override is given, so a
  // reassigned `globalThis.fetch` (e.g. a test mock) is still picked up.
  const doFetch: typeof fetch = config.fetchFn ?? ((input, init) => fetch(input, init))

  function maybeActiveContextHeader(): Record<string, string> {
    return sendActiveContext ? activeContextHeader() : {}
  }

  async function fetchWithTimeout(
    input: string,
    init: RequestInit,
    timeoutMs?: number
  ): Promise<Response> {
    if (!timeoutMs || timeoutMs <= 0) {
      return doFetch(input, init)
    }

    const controller = new AbortController()
    const upstreamSignal = init.signal
    let didTimeout = false
    let removeAbortListener: (() => void) | undefined

    if (upstreamSignal) {
      if (upstreamSignal.aborted) {
        controller.abort()
      } else {
        const abortFromUpstream = () => controller.abort()
        upstreamSignal.addEventListener('abort', abortFromUpstream, { once: true })
        removeAbortListener = () => {
          upstreamSignal.removeEventListener('abort', abortFromUpstream)
        }
      }
    }

    const timeoutId = setTimeout(() => {
      didTimeout = true
      controller.abort()
    }, timeoutMs)

    try {
      return await doFetch(input, {
        ...init,
        signal: controller.signal,
      })
    } catch (error) {
      if (didTimeout) {
        throw new Error(`Request timed out after ${timeoutMs}ms`)
      }

      throw error
    } finally {
      clearTimeout(timeoutId)
      removeAbortListener?.()
    }
  }

  function authHeader(token: string | null): Record<string, string> {
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  async function getHeaders(
    options: RequestInit,
    token: string | null
  ): Promise<Record<string, string>> {
    const defaultHeaders = (await getDefaultHeaders?.()) ?? {}

    return {
      'Content-Type': 'application/json',
      // Omitted until the user explicitly picks a context, so the server can
      // apply its personal fallback (see getActiveContextHeaderValue). A public
      // client (sendActiveContext:false) never sends it.
      ...maybeActiveContextHeader(),
      ...defaultHeaders,
      ...authHeader(token),
      ...(options.headers as Record<string, string> | undefined),
    }
  }

  interface ResponseWithActiveContext {
    response: Response
    activeContext: string | null
  }

  async function sendRequest(
    path: string,
    options: RequestInit,
    token: string | null
  ): Promise<ResponseWithActiveContext> {
    const headers = await getHeaders(options, token)
    const response = await doFetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    })
    return {
      response,
      activeContext: headers[ACTIVE_CONTEXT_HEADER] ?? null,
    }
  }

  async function parseError(response: Response): Promise<ApiError> {
    const contentType = response.headers.get('Content-Type') ?? ''
    let body: unknown = null

    if (contentType.includes('application/json')) {
      try {
        body = (await response.json()) as unknown
      } catch {
        body = null
      }
    } else {
      try {
        body = await response.text()
      } catch {
        body = null
      }
    }

    if (isDesktopUpdateRequiredPayload(body) && response.status === 426) {
      return new DesktopUpdateRequiredError({
        status: response.status,
        statusText: response.statusText,
        payload: body,
      })
    }

    return new ApiError({
      status: response.status,
      statusText: response.statusText,
      body,
    })
  }

  function clearRejectedActiveContext(requestActiveContext: string | null): boolean {
    if (requestActiveContext !== null && getActiveContextHeaderValue() === requestActiveContext) {
      clearActiveContext()
      return true
    }
    return false
  }

  async function throwApiError(
    response: Response,
    requestToken: string | null,
    requestActiveContext: string | null
  ): Promise<never> {
    const error = await parseError(response)
    return throwParsedApiError(error, requestToken, requestActiveContext)
  }

  async function throwParsedApiError(
    error: ApiError,
    requestToken: string | null,
    requestActiveContext: string | null
  ): Promise<never> {
    await handleParsedApiError(error, requestToken, requestActiveContext)
    throw error
  }

  async function handleParsedApiError(
    error: ApiError,
    requestToken: string | null,
    requestActiveContext: string | null
  ): Promise<void> {
    // Recover from a stale/revoked active context (e.g. the user lost a team
    // seat), but do not let a delayed response clear a newer context the user
    // selected while this request was in flight.
    const didClearActiveContext =
      isActiveContextForbidden(error) || isInvalidActiveContext(error)
        ? clearRejectedActiveContext(requestActiveContext)
        : false
    await onResponseError?.(error, {
      requestToken,
      requestActiveContext,
      didClearActiveContext,
    })
  }

  function isReadOnlyRequest(options: RequestInit): boolean {
    const method = (options.method ?? 'GET').toUpperCase()
    return method === 'GET' || method === 'HEAD'
  }

  /**
   * Single transparent retry for a request the server refused because the
   * declared `X-Active-Context` is stale/revoked. Clears the rejected selection
   * and repeats the request once, now header-less, so the server re-resolves its
   * personal fallback — callers never render a transient 403 that the very next
   * request would not have hit. If the retry also fails, the ORIGINAL structured
   * error is preserved so existing stale-context recovery paths still fire.
   *
   * Safe for non-idempotent methods too, which is why uploads share it: an
   * `active_context_forbidden` is decided BEFORE any state changes. The
   * organization-context middleware refuses the request without calling the
   * controller at all, and the one endpoint that re-checks the context itself
   * (`POST /api/v1/audio-batches`, re-authorizing team membership under a lock)
   * does so as the first statement inside its transaction, before any write. So
   * a retried request can never double-apply an effect the first attempt landed.
   */
  async function retryAfterStaleActiveContext(
    result: ResponseWithActiveContext,
    requestToken: string | null,
    retry: () => Promise<ResponseWithActiveContext>
  ): Promise<ResponseWithActiveContext> {
    const error = await parseError(result.response)
    if (!isActiveContextForbidden(error)) {
      return throwParsedApiError(error, requestToken, result.activeContext)
    }

    // Notify platform clients even when the contextless retry succeeds: the UI
    // still needs to leave a now-invalid workspace and explain the recovery.
    await handleParsedApiError(error, requestToken, result.activeContext)
    const retried = await retry()
    if (!retried.response.ok) {
      const retryError = await parseError(retried.response)
      await handleParsedApiError(retryError, requestToken, retried.activeContext)
      throw error
    }
    return retried
  }

  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await tokenStorage.getAccessToken()
    const send = () => sendRequest(path, options, token)
    let result = await send()

    if (!result.response.ok && isReadOnlyRequest(options)) {
      result = await retryAfterStaleActiveContext(result, token, send)
    }

    if (!result.response.ok) {
      return throwApiError(result.response, token, result.activeContext)
    }

    if (result.response.status === 204) {
      return undefined as T
    }

    return result.response.json() as Promise<T>
  }

  async function requestText(path: string, options: RequestInit = {}): Promise<string> {
    const token = await tokenStorage.getAccessToken()
    const send = () => sendRequest(path, options, token)
    let result = await send()

    if (!result.response.ok && isReadOnlyRequest(options)) {
      result = await retryAfterStaleActiveContext(result, token, send)
    }

    if (!result.response.ok) {
      return throwApiError(result.response, token, result.activeContext)
    }

    return result.response.text()
  }

  async function requestBlob(path: string, options: RequestInit = {}): Promise<Blob> {
    const token = await tokenStorage.getAccessToken()
    const send = () => sendRequest(path, options, token)
    let result = await send()

    if (!result.response.ok && isReadOnlyRequest(options)) {
      result = await retryAfterStaleActiveContext(result, token, send)
    }

    if (!result.response.ok) {
      return throwApiError(result.response, token, result.activeContext)
    }

    return result.response.blob()
  }

  async function upload<T>(
    path: string,
    formData: FormData,
    options: UploadOptions = {}
  ): Promise<T> {
    const token = await tokenStorage.getAccessToken()
    const defaultHeaders = (await getDefaultHeaders?.()) ?? {}
    // The header set is rebuilt per attempt so a retry picks up the cleared
    // context and goes out header-less.
    const send = async (): Promise<ResponseWithActiveContext> => {
      const headers = {
        ...maybeActiveContextHeader(),
        ...defaultHeaders,
        ...authHeader(token),
        ...options.headers,
      }
      const response = await fetchWithTimeout(
        `${baseUrl}${path}`,
        {
          method: 'POST',
          headers,
          body: formData,
          signal: options.signal,
        },
        options.timeoutMs
      )
      return { response, activeContext: headers[ACTIVE_CONTEXT_HEADER] ?? null }
    }

    let result = await send()

    // Retry only when the caller opted in AND this attempt actually DECLARED a
    // context: with no header there is no rejected selection to drop, and a
    // second POST would be a pure duplicate. The retry never double-applies an
    // effect (see `retryAfterStaleActiveContext`), but it DOES re-target the
    // write to the server's personal fallback, so the callsite owns that
    // decision via `retryOnStaleActiveContext`.
    if (
      !result.response.ok &&
      result.activeContext !== null &&
      options.retryOnStaleActiveContext === true
    ) {
      result = await retryAfterStaleActiveContext(result, token, send)
    }

    if (!result.response.ok) {
      return throwApiError(result.response, token, result.activeContext)
    }

    return result.response.json() as Promise<T>
  }

  return { request, requestText, requestBlob, upload }
}

function isDesktopUpdateRequiredPayload(value: unknown): value is DesktopUpdateRequiredPayload {
  if (!value || typeof value !== 'object') {
    return false
  }

  const payload = value as Partial<DesktopUpdateRequiredPayload>
  return (
    payload.code === 'desktop_update_required' &&
    typeof payload.minimumSupportedVersion === 'string' &&
    typeof payload.title === 'string' &&
    typeof payload.message === 'string'
  )
}
